/*----- InfoModalを利用したモーダルユーティリティ ----- */
import { h } from 'vue';
import { i18n } from '@/i18n';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';

const t = i18n.t;

/**
 * モーダルアイコン種別
 */
type LegacyIconType = 'success' | 'error' | 'warning' | 'info' | 'question' | 'non';
/**
 * モーダルクローズ時のアクション種別
 */
type ModalCloseAction = 'confirm' | 'deny' | 'dismiss';

/**
 * モーダルの操作結果
 */
export interface ModalActionResult<T = undefined> {
  isConfirmed: boolean;
  isDenied: boolean;
  isDismissed: boolean;
  value?: T;
}

/**
 * モーダル結果のユーティリティ生成
 * @param action - 確定/却下/閉じるのどれか
 * @param value - 追加で保持したい値
 */
export const createModalActionResult = <T>(action: ModalCloseAction, value?: T): ModalActionResult<T> => {
  return {
    isConfirmed: action === 'confirm',
    isDenied: action === 'deny',
    isDismissed: action === 'dismiss',
    value,
  };
};

/**
 * アイコン種別をInfoModal用に変換する
 * @param iconType - LegacySwal由来のアイコン種別
 */
const convertIcon = (iconType: LegacyIconType): '' | 'info' | 'error' | 'loading' => {
  switch (iconType) {
    case 'error':
      return 'error';
    case 'info':
    case 'question':
    case 'success':
    case 'warning':
      return 'info';
    default:
      return '';
  }
};

/**
 * 色スタイルをIcon種別から解決する
 * @param iconType - LegacySwal由来のアイコン種別
 */
const convertColorStyle = (iconType: LegacyIconType): 'primary' | 'error' => {
  return iconType === 'error' ? 'error' : 'primary';
};

/**
 * string/HTMLElementをInfoModalのslotへ変換
 * @param text - 描画対象文字列またはHTMLElement
 */
const convertToSlot = (text: string | HTMLElement) => {
  if (typeof text === 'string') {
    return text;
  }
  const markup = text.outerHTML;
  return () => h('div', { innerHTML: markup });
};

/**
 * HTML文字列をslot関数に変換する
 * @param htmlContent - HTML文字列またはHTMLElement
 */
const convertHtmlSlot = (htmlContent?: string | HTMLElement) => {
  if (!htmlContent) {
    return undefined;
  }
  const markup = typeof htmlContent === 'string' ? htmlContent : htmlContent.outerHTML;
  return () => h('div', { innerHTML: markup });
};

/**
 * 説明文をslot互換に整える
 * @param content - 説明テキスト
 */
const resolveDescription = (content?: string) => {
  if (!content) {
    return undefined;
  }
  return content;
};

/**
 * モーダル表示の追加オプション
 */
interface ModalDisplayOptions {
  enableOutsideClick?: boolean;
  testId?: string;
  useKeyboard?: boolean;
}

/**
 * InfoModalベースの確認モーダル
 * @param iconType - 表示アイコン
 * @param titleText - タイトルテキスト
 * @param confirmButtonText - OKボタンのラベル
 * @param denyButtonText - キャンセルボタンのラベル
 * @param contentText - モーダル向けテキスト
 * @param htmlText - HTML文字列
 * @param modalOptions - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openConfirmModal = async (
  iconType: LegacyIconType,
  titleText: string | HTMLElement,
  confirmButtonText: string,
  denyButtonText: string,
  contentText?: string,
  htmlText?: string | HTMLElement,
  modalOptions?: ModalDisplayOptions
): Promise<ModalActionResult<any>> => {
  const htmlSlot = convertHtmlSlot(htmlText);
  const modalResult = await showInfoModal({
    modalTitle: convertToSlot(titleText),
    modalDescription: htmlSlot ? undefined : resolveDescription(contentText),
    modalContent: htmlSlot,
    backButtonLabel: denyButtonText,
    nextButtonLabel: confirmButtonText,
    icon: convertIcon(iconType),
    colorStyle: convertColorStyle(iconType),
    enableOutsideClick: modalOptions?.enableOutsideClick ?? true,
    testId: modalOptions?.testId,
    isCenterContent: true,
    useKeyboard: modalOptions?.useKeyboard ?? false,
  });
  return modalResult ? createModalActionResult('confirm') : createModalActionResult('deny');
};

/**
 * OKボタンのみのInfoModal
 * @param iconType - 表示アイコン
 * @param titleText - タイトルテキスト
 * @param confirmButtonText - OKボタンのラベル
 * @param contentText - モーダル向けテキスト
 * @param htmlText - HTML文字列
 * @param modalOptions - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openOkInformationModal = async (
  iconType: LegacyIconType,
  titleText: string | HTMLElement,
  confirmButtonText: string,
  contentText?: string,
  htmlText?: string | HTMLElement,
  modalOptions?: ModalDisplayOptions
): Promise<ModalActionResult<any>> => {
  let content = contentText;
  let html = htmlText;

  if (content && content.includes('\n') && !html) {
    html = content.replace(/\n/g, '<br>');
    content = undefined;
  }

  const htmlSlot = convertHtmlSlot(html);
  const modalResult = await showInfoModal({
    modalTitle: convertToSlot(titleText),
    modalDescription: htmlSlot ? undefined : resolveDescription(content),
    modalContent: htmlSlot,
    backButtonLabel: '',
    nextButtonLabel: confirmButtonText,
    icon: convertIcon(iconType),
    colorStyle: convertColorStyle(iconType),
    enableOutsideClick: false,
    isCenterContent: true,
    useKeyboard: modalOptions?.useKeyboard ?? false,
  });
  return modalResult ? createModalActionResult('confirm') : createModalActionResult('dismiss');
};

/**
 * ファイルなどの保存確認用モーダル
 * @param options - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openConfirmToSaveModal = async (options?: ModalDisplayOptions): Promise<ModalActionResult<any>> => {
  return openConfirmModal('question', t('保存しますか'), t('保存する'), t('キャンセル'), undefined, undefined, options);
};

/**
 * 保存成功モーダル
 * @param options - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openSuccessToSaveModal = async (options?: ModalDisplayOptions): Promise<ModalActionResult<any>> => {
  return openOkInformationModal('success', t('成功'), 'OK', t('保存しました'), undefined, options);
};

/**
 * 保存失敗モーダル
 * @param options - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openFailToSavedModal = async (options?: ModalDisplayOptions): Promise<ModalActionResult<any>> => {
  return openOkInformationModal('error', t('エラー'), 'OK', t('保存に失敗しました'), undefined, options);
};

/**
 * 取得失敗モーダル
 * @param options - 追加オプション（外部クリック可否/テストIDなど）
 * @returns モーダル操作結果
 */
export const openFailToAcquireModal = async (options?: ModalDisplayOptions): Promise<ModalActionResult<any>> => {
  return openOkInformationModal('error', t('エラー'), 'OK', t('取得に失敗しました'), undefined, options);
};

/**
 * ローディングモーダルを表示し、非同期処理の完了を待つ
 * InfoModalManager経由でローディングを表示する
 * @param loadingText - ローディング文言
 * @param asyncFunction - 実行する非同期処理
 * @param args - 非同期処理に渡す引数
 * @param displayText - 説明文
 * @returns 非同期処理の戻り値
 */
export const openLoadingModal = async <T>(
  loadingText: string,
  asyncFunction: (...args: any[]) => Promise<T>,
  args: any[],
  displayText?: string
): Promise<T> => {
  let closeModal: (() => void) | undefined;
  const modalPromise = showInfoModal({
    modalTitle: loadingText,
    modalDescription: displayText,
    icon: 'loading',
    enableOutsideClick: false,
    backButtonLabel: '',
    nextButtonLabel: '',
    isDisabledScroll: true,
    /**
     * モーダルを閉じる内部コールバックを保持する
     * @param helpers - InfoModalの制御ヘルパー
     */
    onInitialized: (helpers) => {
      /**
       * モーダルを閉じる
       */
      closeModal = () => helpers.onNext();
    },
  });

  try {
    const result = await asyncFunction(...args);
    closeModal?.();
    await modalPromise;
    return result;
  } catch (error) {
    closeModal?.();
    await modalPromise;
    throw error;
  }
};
